import { useEffect, useRef, type ReactNode } from "react";
import { createPortal } from "react-dom";

interface PopoverProps {
  anchor: HTMLElement | null;
  onClose: () => void;
  width?: number;
  align?: "left" | "right";
  children: ReactNode;
}

// Rendered via a Portal so `position: fixed` always anchors to the viewport
// — not to a transform-ed ancestor (cards lift on hover with translateY,
// which would otherwise re-anchor the popover and push it off-screen).
export function Popover({ anchor, onClose, width = 240, align = "left", children }: PopoverProps) {
  const ref = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    const id = window.setTimeout(() => document.addEventListener("mousedown", onDown), 0);
    return () => {
      window.clearTimeout(id);
      document.removeEventListener("mousedown", onDown);
    };
  }, [onClose]);
  if (!anchor) return null;
  const rect = anchor.getBoundingClientRect();
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const margin = 8;
  // Clamp horizontally so the popover never runs off either edge.
  const rawLeft = align === "right" ? rect.right - width : rect.left;
  const left = Math.max(margin, Math.min(rawLeft, vw - width - margin));
  // Flip up when there isn't enough room below AND there's more room above (B1:
  // the person/status picker near the screen bottom used to open off-screen).
  const spaceBelow = vh - rect.bottom;
  const spaceAbove = rect.top;
  const openUp = spaceBelow < 260 && spaceAbove > spaceBelow;
  const vertical = openUp ? { bottom: vh - rect.top + 4 } : { top: rect.bottom + 4 };
  const maxHeight = Math.max(160, (openUp ? spaceAbove : spaceBelow) - margin - 4);
  return createPortal(
    <div
      ref={ref}
      style={{
        position: "fixed",
        ...vertical,
        left,
        width,
        maxHeight,
        overflowY: "auto",
        zIndex: 400,
        background: "var(--surface-elev)",
        color: "var(--ink-1)",
        borderRadius: 10,
        border: "1px solid var(--hairline-strong)",
        boxShadow: "0 10px 28px rgba(13,62,99,0.14), 0 2px 6px rgba(13,62,99,0.06)",
        fontFamily: "Inter Variable, Inter, system-ui, sans-serif",
      }}
    >
      {children}
    </div>,
    document.body,
  );
}
